/**
 * BookingListCard — the mobile + tablet card for one booking (BON-130).
 *
 * Whole card is the row click-through → `/admin/bookings/:id`. The workshop name and
 * venue name are nested links into the current dashboards shell
 * (`/{shell}/workshops/:id`, `/{shell}/venues/:id`); both stop propagation so
 * they don't also fire the card navigation. Each falls back to plain text when
 * its id is missing.
 */
import { MapPin, Ticket } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import type { BookingListItem } from '../../api/bookingsList';
import {
  shellBasePath,
  workshopDetailUrlFor,
  type WorkshopShell,
} from '../dashboards/workshopDetailUrlFor';

import {
  customerName,
  displaySource,
  displayTotal,
  ticketsLabel,
  workshopDateLabel,
  workshopTimeLabel,
} from './bookingListModel';
import { BookingStatusPill } from './BookingStatusPill';
import { ShellLink } from './ShellLink';

const LINK = 'hover:text-moss-600 hover:underline dark:hover:text-moss-400';

export function BookingListCard({
  booking,
  shell,
}: {
  booking: BookingListItem;
  shell: WorkshopShell;
}) {
  const navigate = useNavigate();
  const go = () => navigate(`/admin/bookings/${booking.id}`);
  const w = booking.workshop;
  const when = `${workshopDateLabel(w)} · ${workshopTimeLabel(w)}`;
  const workshopHref = w.id ? workshopDetailUrlFor(shell, w.id) : null;
  const venueHref = w.venue.id ? `${shellBasePath(shell)}/venues/${w.venue.id}` : null;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={go}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go();
        }
      }}
      className="cursor-pointer rounded-xl border border-stone-200 bg-white p-4 text-left shadow-sm transition hover:border-stone-300 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-moss-500 dark:border-stone-700 dark:bg-[#1a1f1c]"
    >
      {/* Row 1: name + code | status */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-bold text-bark-900 dark:text-[#e8ece9]">
            {customerName(booking.customer)}
          </div>
          <div className="mt-0.5 font-mono text-xs text-stone-500">{booking.displayRef ?? '—'}</div>
        </div>
        <BookingStatusPill booking={booking} />
      </div>

      {/* Row 2: workshop · date · time */}
      <div className="mt-3 flex items-center gap-2 text-sm">
        <Ticket size={15} className="flex-shrink-0 text-stone-400" />
        <span className="truncate font-semibold text-bark-900 dark:text-stone-100">
          <ShellLink href={workshopHref} className={LINK}>
            {w.name ?? 'Workshop'}
          </ShellLink>
          {` · ${when}`}
        </span>
      </div>

      {/* Row 3: venue · region */}
      <div className="mt-1.5 flex items-center gap-2 text-sm text-stone-600 dark:text-stone-400">
        <MapPin size={15} className="flex-shrink-0 text-stone-400" />
        <span className="truncate">
          <ShellLink href={venueHref} className={`text-stone-600 dark:text-stone-300 ${LINK}`}>
            {w.venue.name}
          </ShellLink>
          {' · '}
          {w.region.name}
        </span>
      </div>

      {/* Row 4: tickets + source | total */}
      <div className="mt-3 flex items-center justify-between border-t border-stone-100 pt-3 dark:border-stone-700/60">
        <div className="flex items-center gap-3">
          <span className="inline-flex items-center rounded-full border border-stone-200 px-2.5 py-1 text-xs text-stone-600 dark:border-stone-700 dark:text-stone-300">
            {ticketsLabel(booking.attendeeCount)}
          </span>
          <span className="text-sm text-stone-500">{displaySource(booking)}</span>
        </div>
        <span className="font-bold text-bark-900 dark:text-[#e8ece9]">{displayTotal(booking)}</span>
      </div>
    </div>
  );
}
